import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/shared/ui/atoms/button'
import { NoWifiIcon } from '@/shared/ui/icons/no-wifi-icon'
import { useNetworkStatus } from '@/shared/stores/network-status.store'
import './offline-modal.css'

interface OfflineModalProps {
  isOpen: boolean
  onClose: () => void
  onRetry: () => void
}

/**
 * Modern offline modal with animated no-wifi icon.
 *
 * Auto-closes when `isOnline` becomes true — triggers `onRetry` which
 * the controller uses to resume the pending navigation or reset the
 * error boundary.
 */
export function OfflineModal({ isOpen, onClose, onRetry }: OfflineModalProps) {
  const { isOnline } = useNetworkStatus()
  const { t } = useTranslation()

  // Auto-recover when connection is restored
  useEffect(() => {
    if (isOpen && isOnline) {
      onRetry()
    }
  }, [isOpen, isOnline, onRetry])

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [isOpen, onClose])

  if (!isOpen) return null

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose()
  }

  return (
    <div
      className='offline-modal-backdrop'
      role='dialog'
      aria-modal='true'
      aria-labelledby='offline-modal-title'
      onClick={handleBackdropClick}
    >
      <div className='offline-modal-card'>
        <button
          type='button'
          className='offline-modal-close'
          onClick={onClose}
          aria-label={t('close')}
        >
          ×
        </button>

        <div className='offline-modal-content'>
          <div className='offline-modal-icon'>
            <NoWifiIcon size={120} />
          </div>

          <h2 id='offline-modal-title' className='offline-modal-title'>
            {t('internet_connection_lost')}
          </h2>

          <p className='offline-modal-description'>
            {t('offline_modal_description')}
          </p>

          <div className='offline-modal-actions'>
            <Button onClick={onClose} type='default'>
              {t('close')}
            </Button>
            <Button onClick={onRetry} type='primary'>
              {t('retry')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default OfflineModal
